<script setup lang="ts">
import { bitable } from '@lark-base-open/js-sdk'
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  FileText, Download, Trash2, Settings, Loader2, Link2, Eye, EyeOff, Copy, ChevronDown, ChevronUp, AlertCircle
} from 'lucide-vue-next'
import { toast } from '@/utils/toast'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import EmptyTemplateState from '@/components/EmptyTemplateState.vue'
import { templateApi } from '@/services/api'
import {
  loadAllFields,
  flattenFieldsForFilename,
  getRecordData
} from '@/utils/fieldManager'
import { useUIStore } from '@/stores/ui'
import { useTenantStore } from '@/stores/tenant'
import { storeToRefs } from 'pinia'
import type { Template } from '@/types'

interface Props {
  activeTemplate: Template | null
  allTemplates: Template[]
  baseToken: string
  tableId: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'template-updated': []
  'template-deleted': []
  'create-template': []
}>()

const { t } = useI18n()
const router = useRouter()
const uiStore = useUIStore()
const tenantStore = useTenantStore()
const { isPro, hasUnlimitedQuota } = storeToRefs(tenantStore)

// State
const uploading = ref(false)
const downloading = ref(false)
const uploadedFile = ref<string | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)
const showDeleteDialog = ref(false)
const deleting = ref(false)

// Record selection state (internal only, no UI display)
const loadingRecordSelection = ref(false)

// Template name editing
const tempName = ref('')
const savingName = ref(false)

// Output filename pattern
const outputPattern = ref('{Template_Name}-{Record_ID}')
const savingPattern = ref(false)
const currentRecordData = ref<Record<string, any> | null>(null)
const filenameVariables = ref<Array<{ variable: string; isSystem?: boolean; isLinked?: boolean }>>([])
const outputPatternInputRef = ref<HTMLInputElement | null>(null)
const variableSearch = ref('')

// Pro Features
const enablePdf = ref(false)
const enableCompression = ref(false)
const compressionQuality = ref(80)
const saveToBaseEnabled = ref(false)
const attachmentFieldId = ref<string | null>(null)
const attachmentWriteMode = ref<'replace' | 'append'>('append')
const statusFieldId = ref<string | null>(null)
const webhookUrl = ref<string>('')
const webhookBearerToken = ref<string>('')
const webhookEnabled = ref(false)
const showWebhookToken = ref(false)
const showWebhookApiKey = ref(false)
const showHttpTrigger = ref(false)
const savingProSettings = ref(false)
const attachmentFields = ref<Array<{ id: string; name: string }>>([])
const textFields = ref<Array<{ id: string; name: string }>>([])

// Field change listeners
const unsubscribeFieldAdd = ref<(() => void) | null>(null)
const unsubscribeFieldDelete = ref<(() => void) | null>(null)
const unsubscribeFieldModify = ref<(() => void) | null>(null)

// Shared debounce timer for all field change events
let fieldChangeDebounce: number | null = null

// HTTP Trigger availability - requires save to base with attachment field
const isHttpTriggerAvailable = computed(() => {
  return saveToBaseEnabled.value && attachmentFieldId.value !== null
})

// Filtered filename variables based on search
const filteredFilenameVariables = computed(() => {
  if (!variableSearch.value.trim()) {
    return filenameVariables.value
  }
  const searchLower = variableSearch.value.toLowerCase()
  return filenameVariables.value.filter(field =>
    field.variable.toLowerCase().includes(searchLower)
  )
})

// Computed property for output filename preview
const outputFilenamePreview = computed(() => {
  if (!outputPattern.value || !currentRecordData.value) {
    return outputPattern.value || ''
  }

  let preview = outputPattern.value

  preview = preview.replace(/\{([^}]+)\}/g, (match: string, variable: string) => {
    const parts = variable.split('.')
    let value: any = currentRecordData.value

    for (const part of parts) {
      if (value && typeof value === 'object' && part in value) {
        value = value[part]
      } else {
        return match
      }
    }

    if (value === null || value === undefined) {
      return ''
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? String(value[0]) : ''
    }
    if (typeof value === 'object') {
      return value.name || value.text || JSON.stringify(value)
    }
    return String(value)
  })

  if (!preview.endsWith('.docx')) {
    preview += '.docx'
  }

  return preview
})

// Computed property for sample webhook payload
const sampleWebhookPayload = computed(() => {
  if (!currentRecordData.value || !props.activeTemplate) {
    return null
  }

  // Filter to only primitive root fields (no arrays, objects, or complex structures)
  const filterRootFields = (data: Record<string, any>): Record<string, any> => {
    const root: Record<string, any> = {}
    for (const [key, value] of Object.entries(data)) {
      // Only include primitive values (string, number, boolean, null)
      if (value === null ||
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean') {
        root[key] = value
      }
    }
    return root
  }

  const filename = outputFilenamePreview.value || `${props.activeTemplate.name}.docx`

  return {
    url: 'https://example.com/api/bases/bascXXXX/tables/tblXXXX/generations/genXXXX/download?expires=...&signature=...',
    filename: filename,
    fields: filterRootFields(currentRecordData.value),
  }
})

const copySamplePayload = () => {
  if (!sampleWebhookPayload.value) return
  copyToClipboard(JSON.stringify(sampleWebhookPayload.value, null, 2))
}

// Webhook generation URL for HTTP trigger
const webhookGenerateUrl = computed(() => {
  if (!props.activeTemplate) return ''
  const origin = window.location.origin
  return `${origin}/api/webhook/templates/${props.activeTemplate.id}/generate`
})

const copyWebhookUrl = () => {
  if (!webhookGenerateUrl.value) return
  copyToClipboard(webhookGenerateUrl.value)
}

// Helper function to copy text (works in iframes/restricted contexts)
const copyToClipboard = (text: string) => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()

  try {
    document.execCommand('copy')
    toast.success(t('copiedToClipboard'))
  } catch (err) {
    console.error('Failed to copy:', err)
    toast.error(t('failedToCopyToClipboard'))
  } finally {
    document.body.removeChild(textarea)
  }
}

// Insert variable at cursor position
const insertVariableAtCursor = (variable: string) => {
  const input = outputPatternInputRef.value
  if (!input) {
    // Fallback: append to end
    outputPattern.value += `{${variable}}`
    return
  }

  const startPos = input.selectionStart || 0
  const endPos = input.selectionEnd || 0
  const textBefore = outputPattern.value.substring(0, startPos)
  const textAfter = outputPattern.value.substring(endPos)
  const variableText = `{${variable}}`

  outputPattern.value = textBefore + variableText + textAfter

  // Set cursor position after the inserted variable
  setTimeout(() => {
    const newCursorPos = startPos + variableText.length
    input.setSelectionRange(newCursorPos, newCursorPos)
    input.focus()
  }, 0)
}

// Template name editing
const saveTemplateName = async () => {
  const trimmedName = tempName.value?.trim()

  if (!props.activeTemplate || !trimmedName) {
    toast.warning(t('templateNameRequired'))
    tempName.value = props.activeTemplate?.name || ''
    return
  }

  if (trimmedName === props.activeTemplate.name) {
    return
  }

  try {
    savingName.value = true
    await templateApi.update(props.baseToken, props.tableId, props.activeTemplate.id, {
      name: trimmedName
    })

    toast.success(t('templateNameUpdated'))
    emit('template-updated')
  } catch (error) {
    console.error('Failed to update template name:', error)
    toast.error(error instanceof Error ? error.message : t('failedToUpdateTemplateName'))
    tempName.value = props.activeTemplate.name
  } finally {
    savingName.value = false
  }
}

// Output filename pattern
const saveOutputPattern = async () => {
  if (!props.activeTemplate) return

  try {
    savingPattern.value = true
    await templateApi.update(props.baseToken, props.tableId, props.activeTemplate.id, {
      output_filename_pattern: outputPattern.value
    })

    toast.success(t('outputPatternSaved'))
    emit('template-updated')
  } catch (error) {
    console.error('Failed to save output pattern:', error)
    toast.error(error instanceof Error ? error.message : t('failedToSaveOutputPattern'))
  } finally {
    savingPattern.value = false
  }
}

// File operations
const handleUpload = () => {
  if (!props.activeTemplate) return
  fileInputRef.value?.click()
}

const handleFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return

  if (!file.name.toLowerCase().endsWith('.docx')) {
    toast.error(t('invalidFileType'))
    target.value = ''
    return
  }

  try {
    uploading.value = true
    await templateApi.uploadFile(props.baseToken, props.tableId, props.activeTemplate!.id, file)

    uploadedFile.value = file.name
    toast.success(t('templateUploaded'))
    emit('template-updated')
  } catch (error: any) {
    console.error('Failed to upload template:', error)

    // Check if this is a validation error with specific details
    if (error?.validationErrors && Array.isArray(error.validationErrors)) {
      const errorMessages = error.validationErrors.map((err: any) => {
        const errorTypeKey = `errorType_${err.type}`
        const errorType = t(errorTypeKey)
        return `• ${errorType}: ${err.message}${err.context ? `\n  Context: "${err.context}"` : ''}`
      }).join('\n')

      toast.error(`${t('templateValidationFailed')}\n\n${t('placeholderErrors')}\n${errorMessages}\n\n${t('fixTemplateErrors')}`, 10000) // Show for 10 seconds
    } else {
      toast.error(error instanceof Error ? error.message : t('failedToUploadTemplate'))
    }
  } finally {
    uploading.value = false
    target.value = ''
  }
}

const handleDownload = async () => {
  if (!props.activeTemplate || !uploadedFile.value) return

  try {
    downloading.value = true
    await templateApi.downloadFile(
      props.baseToken,
      props.tableId,
      props.activeTemplate.id,
      uploadedFile.value
    )
  } catch (error) {
    console.error('Failed to download template:', error)
    toast.error(error instanceof Error ? error.message : t('failedToDownloadTemplate'))
  } finally {
    downloading.value = false
  }
}

// Pro Features operations
const saveProSettings = async () => {
  if (!props.activeTemplate) return;

  try {
    savingProSettings.value = true;
    await templateApi.update(props.baseToken, props.tableId, props.activeTemplate.id, {
      enable_pdf: enablePdf.value,
      enable_compression: enableCompression.value,
      compression_quality: compressionQuality.value,
      save_field_id: saveToBaseEnabled.value ? attachmentFieldId.value : null,
      attachment_write_mode: saveToBaseEnabled.value ? attachmentWriteMode.value : null,
      status_field_id: saveToBaseEnabled.value ? statusFieldId.value : null,
      webhook_url: webhookEnabled.value ? webhookUrl.value : null,
      webhook_bearer_token: webhookEnabled.value ? (webhookBearerToken.value || null) : null,
    });

    toast.success(t('proSettingsSaved'));
    emit('template-updated');
  } catch (error) {
    console.error('Failed to save pro settings:', error);
    toast.error(error instanceof Error ? error.message : t('failedToSaveProSettings'));
  } finally {
    savingProSettings.value = false;
  }
};

// Load attachment and text fields
const loadFieldsForProFeatures = async () => {
  try {
    const selection = await bitable.base.getSelection();
    if (!selection.tableId) return;

    const table = await bitable.base.getTableById(selection.tableId);
    const fieldMetaList = await table.getFieldMetaList();

    // Filter attachment fields
    attachmentFields.value = fieldMetaList
      .filter((field: any) => field.type === 17) // 17 is attachment type
      .map((field: any) => ({ id: field.id, name: field.name }));

    // Filter text fields
    textFields.value = fieldMetaList
      .filter((field: any) => field.type === 1) // 1 is text type
      .map((field: any) => ({ id: field.id, name: field.name }));
  } catch (error) {
    console.error('Failed to load fields for pro features:', error);
  }
};

// Reload all field-dependent data
const reloadFieldData = async () => {
  await loadFilenameVariables()
  await loadFieldsForProFeatures()
}

// Setup field change listeners
const setupFieldListeners = async () => {
  try {
    const selection = await bitable.base.getSelection()
    if (!selection.tableId) return

    const table = await bitable.base.getTableById(selection.tableId)

    unsubscribeFieldAdd.value = table.onFieldAdd(async () => {
      if (fieldChangeDebounce) clearTimeout(fieldChangeDebounce)
      fieldChangeDebounce = window.setTimeout(async () => {
        await reloadFieldData()
        toast.info(t('fieldsUpdated'))
        fieldChangeDebounce = null
      }, 100)
    })

    unsubscribeFieldDelete.value = table.onFieldDelete(async () => {
      if (fieldChangeDebounce) clearTimeout(fieldChangeDebounce)
      fieldChangeDebounce = window.setTimeout(async () => {
        await reloadFieldData()
        toast.info(t('fieldsUpdated'))
        fieldChangeDebounce = null
      }, 100)
    })

    unsubscribeFieldModify.value = table.onFieldModify(async () => {
      if (fieldChangeDebounce) clearTimeout(fieldChangeDebounce)
      fieldChangeDebounce = window.setTimeout(async () => {
        await reloadFieldData()
        toast.info(t('fieldsUpdated'))
        fieldChangeDebounce = null
      }, 100)
    })
  } catch (error) {
    console.error('Failed to setup field listeners:', error)
  }
}

// Cleanup listeners
const cleanupListeners = () => {
  // Clear pending debounce timer
  if (fieldChangeDebounce) {
    clearTimeout(fieldChangeDebounce)
    fieldChangeDebounce = null
  }

  // Unsubscribe from events
  if (unsubscribeFieldAdd.value) {
    unsubscribeFieldAdd.value()
    unsubscribeFieldAdd.value = null
  }
  if (unsubscribeFieldDelete.value) {
    unsubscribeFieldDelete.value()
    unsubscribeFieldDelete.value = null
  }
  if (unsubscribeFieldModify.value) {
    unsubscribeFieldModify.value()
    unsubscribeFieldModify.value = null
  }
}

// Delete
const handleDeleteTemplate = () => {
  if (!props.activeTemplate) return
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  if (!props.activeTemplate) return

  try {
    deleting.value = true
    await templateApi.delete(props.baseToken, props.tableId, props.activeTemplate.id)

    showDeleteDialog.value = false
    toast.success(t('templateDeleted'))
    emit('template-deleted')

    // Navigate to generate page after deletion
    router.push('/')
  } catch (error) {
    console.error('Failed to delete template:', error)
    toast.error(error instanceof Error ? error.message : t('failedToDeleteTemplate'))
  } finally {
    deleting.value = false
  }
}

// Load current record data - auto-select first record if none selected
const loadCurrentRecordData = async (forceFirstRecord: boolean = false) => {
  try {
    loadingRecordSelection.value = true
    const selection = await bitable.base.getSelection()
    let recordId = selection.recordId
    let tableId = selection.tableId

    if (!tableId) {
      currentRecordData.value = null
      return
    }

    const table = await bitable.base.getTableById(tableId)
    const recordIdList = await table.getRecordIdList()

    // If no records exist in the table
    if (recordIdList.length === 0) {
      currentRecordData.value = null
      return
    }

    // If no record is selected or forced to use first record, use the first record
    if ((!recordId || forceFirstRecord) && recordIdList.length > 0) {
      recordId = recordIdList[0] || null
    }

    if (recordId && tableId) {
      currentRecordData.value = await getRecordData(tableId, recordId, {
        maxDepth: 5,
        templateName: props.activeTemplate?.name
      })
    } else {
      currentRecordData.value = null
    }
  } catch (error) {
    console.error('Failed to load current record data:', error)
    currentRecordData.value = null
  } finally {
    loadingRecordSelection.value = false
  }
}

// Load filename variables
const loadFilenameVariables = async () => {
  try {
    const selection = await bitable.base.getSelection()
    if (selection.tableId) {
      const allFields = await loadAllFields(selection.tableId, 5, true)
      filenameVariables.value = flattenFieldsForFilename(allFields)
    }
  } catch (error) {
    console.error('Failed to load filename variables:', error)
  }
}

// Watch for "Save to Base" checkbox - clear attachment field when unchecked
watch(saveToBaseEnabled, (newValue) => {
  if (!newValue) {
    attachmentFieldId.value = null
    statusFieldId.value = null
  }
})

// Watch for table changes - reload field lists and re-setup listeners when table changes
watch(() => props.tableId, async (newTableId, oldTableId) => {
  if (newTableId && newTableId !== oldTableId) {
    // Cleanup old listeners for the previous table
    cleanupListeners()

    // Reload field data for the new table
    await loadFilenameVariables()
    await loadFieldsForProFeatures()

    // Setup new listeners for the new table
    await setupFieldListeners()
  }
})

// Watch for template changes
watch(() => props.activeTemplate, (newTemplate) => {
  if (newTemplate) {
    uploadedFile.value = newTemplate.r2_key
      ? (newTemplate.original_filename || `${newTemplate.name}.docx`)
      : null
    outputPattern.value = newTemplate.output_filename_pattern || '{Template_Name}-{Record_ID}'
    tempName.value = newTemplate.name
    // Load pro settings
    enablePdf.value = !!newTemplate.enable_pdf
    enableCompression.value = !!newTemplate.enable_compression
    compressionQuality.value = newTemplate.compression_quality || 80
    saveToBaseEnabled.value = !!newTemplate.save_field_id
    attachmentFieldId.value = newTemplate.save_field_id || null
    attachmentWriteMode.value = (newTemplate.attachment_write_mode as 'replace' | 'append') || 'append'
    statusFieldId.value = newTemplate.status_field_id || null
    webhookUrl.value = newTemplate.webhook_url || ''
    webhookBearerToken.value = newTemplate.webhook_bearer_token || ''
    webhookEnabled.value = !!newTemplate.webhook_url
    loadCurrentRecordData()
  } else {
    // Clear all state when template becomes null (e.g., when switching tables)
    uploadedFile.value = null
    outputPattern.value = '{Template_Name}-{Record_ID}'
    tempName.value = ''
    enablePdf.value = false
    enableCompression.value = false
    compressionQuality.value = 80
    saveToBaseEnabled.value = false
    attachmentFieldId.value = null
    attachmentWriteMode.value = 'append'
    statusFieldId.value = null
    webhookUrl.value = ''
    webhookBearerToken.value = ''
    webhookEnabled.value = false
    currentRecordData.value = null
  }
}, { immediate: true })

onMounted(async () => {
  await loadFilenameVariables()
  await loadFieldsForProFeatures()
  // Auto-select first record on mount if none selected
  await loadCurrentRecordData(true)

  // Setup field change listeners
  await setupFieldListeners()

  // Listen for record selection changes
  bitable.base.onSelectionChange(async () => {
    await loadCurrentRecordData(false)
  })
})

onUnmounted(() => {
  cleanupListeners()
})
</script>

<template>
  <!-- Empty State - No Templates -->
  <EmptyTemplateState
    v-if="!allTemplates || allTemplates.length === 0"
    @create-template="emit('create-template')"
  />

  <div v-else class="h-full bg-white flex flex-col overflow-hidden">
    <div class="flex-1 overflow-y-auto">
      <div class="max-w-4xl mx-auto w-full p-4 sm:p-6 md:p-8">
        <div class="space-y-4 md:space-y-6">
          <!-- Template Name -->
          <div class="bg-gray-50 border border-gray-300 rounded-lg p-4 md:p-6">
            <label class="block text-sm font-semibold text-gray-800 mb-3">{{ t('templateName') }}</label>
            <div class="flex flex-col sm:flex-row gap-3">
              <input
                v-model="tempName"
                type="text"
                class="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                :disabled="savingName"
                @keyup.enter="saveTemplateName"
              />
              <button
                @click="saveTemplateName"
                :disabled="savingName"
                class="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium"
              >
                <Loader2 v-if="savingName" class="animate-spin" :size="16" />
                {{ savingName ? t('saving') : t('save') }}
              </button>
            </div>
          </div>

          <!-- Template File -->
          <div class="bg-gray-50 border border-gray-300 rounded-lg p-4 md:p-6">
            <h3 class="text-sm font-semibold text-gray-800 mb-4">{{ t('templateFile') }}</h3>
            <input
              ref="fileInputRef"
              type="file"
              accept=".docx"
              class="hidden"
              @change="handleFileSelect"
            />

            <div v-if="uploadedFile" class="space-y-3">
              <!-- File Info with Download -->
              <div class="flex items-center gap-3 p-3 bg-white border border-gray-300 rounded-lg">
                <FileText :size="20" class="text-blue-600 flex-shrink-0" />
                <span class="flex-1 text-sm font-medium text-gray-800 truncate">{{ uploadedFile }}</span>
                <button
                  @click="handleDownload"
                  :disabled="downloading"
                  class="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                  :title="t('download')"
                >
                  <Download v-if="!downloading" :size="18" />
                  <span v-else class="w-[18px] h-[18px] border-2 border-gray-500 border-t-transparent rounded-full animate-spin block"></span>
                </button>
              </div>

              <!-- Replace Button -->
              <button
                @click="handleUpload"
                :disabled="uploading"
                class="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium"
              >
                <span v-if="uploading" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                <span v-else>{{ t('replaceTemplate') }}</span>
              </button>
            </div>

            <div v-else class="text-center py-8">
              <FileText :size="48" class="text-gray-400 mx-auto mb-4" />
              <p class="text-sm text-gray-600 mb-4">{{ t('noTemplateFileUploaded') }}</p>
              <button
                @click="handleUpload"
                :disabled="uploading"
                class="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
              >
                <span v-if="uploading" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                {{ uploading ? t('uploadingTemplate') : t('uploadTemplate') }}
              </button>
            </div>
          </div>

          <!-- Output Filename Pattern -->
          <div class="bg-gray-50 border border-gray-300 rounded-lg p-6">
            <h3 class="text-sm font-semibold text-gray-800 mb-2">{{ t('outputFilenamePattern') }}</h3>
            <p class="text-xs text-gray-600 mb-4">{{ t('outputFilenamePatternDesc') }}</p>

            <!-- Available Variables -->
            <div class="mb-4">
              <p class="text-xs font-medium text-gray-700 mb-2">{{ t('availableVariables') }}</p>

              <!-- Search Box -->
              <input
                v-model="variableSearch"
                type="text"
                :placeholder="t('searchVariables')"
                class="w-full px-3 py-1.5 mb-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
              />

              <!-- Variable Buttons -->
              <div class="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                <button
                  v-for="field in filteredFilenameVariables"
                  :key="field.variable"
                  @click="insertVariableAtCursor(field.variable)"
                  class="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 rounded text-xs hover:bg-blue-50 hover:border-blue-500 transition-colors"
                >
                  <component v-if="field.isSystem" :is="Settings" class="w-3 h-3 text-gray-600" />
                  <component v-else-if="field.isLinked" :is="Link2" class="w-3 h-3 text-blue-600" />
                  <span class="font-mono text-blue-600">{{ '{' + field.variable + '}' }}</span>
                </button>
              </div>
              <p v-if="filteredFilenameVariables.length === 0" class="text-xs text-gray-500 mt-2">{{ t('noVariablesFound') }}</p>
            </div>

            <!-- Input with Save Button -->
            <div class="flex gap-2">
              <input
                ref="outputPatternInputRef"
                v-model="outputPattern"
                type="text"
                class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="{Template_Name}-{Record_ID}"
              />
              <button
                @click="saveOutputPattern"
                :disabled="savingPattern"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
              >
                <span v-if="savingPattern" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                {{ savingPattern ? t('saving') : t('save') }}
              </button>
            </div>

            <!-- Live Preview -->
            <div v-if="outputFilenamePreview" class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p class="text-xs text-gray-600 mb-1">{{ t('preview') }}</p>
              <p class="text-sm font-medium text-gray-800 font-mono">{{ outputFilenamePreview }}</p>
            </div>
          </div>

          <!-- Pro Features -->
          <div
            :class="[
              'rounded-lg p-6 relative overflow-hidden',
              isPro
                ? 'bg-gray-50 border border-gray-300'
                : 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300'
            ]"
          >
            <div v-if="!isPro" class="absolute top-3 right-3">
              <span class="bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full">PRO</span>
            </div>

            <h3 class="text-sm font-semibold text-gray-800 mb-4">{{ t('proFeature') }}</h3>

            <div class="space-y-4" :class="{ 'opacity-60 pointer-events-none': !isPro }">
              <!-- Option 1: Generate as PDF -->
              <div class="relative">
                <label class="flex items-start gap-3 p-4 bg-white border border-gray-300 rounded-lg hover:border-gray-400 transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="enablePdf"
                    :disabled="!isPro"
                    class="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div class="flex-1">
                    <span class="text-sm font-semibold text-gray-800">{{ t('generateAsPdf') }}</span>
                    <p class="text-xs text-gray-600 mt-1">{{ hasUnlimitedQuota ? t('generateAsPdfDescUnlimited') : t('generateAsPdfDesc') }}</p>
                  </div>
                </label>
              </div>

              <!-- Option 1.5: Compress Images -->
              <div class="border border-gray-300 rounded-lg bg-white">
                <label class="flex items-start gap-3 p-4 cursor-pointer hover:bg-blue-50 transition-colors">
                  <input
                    type="checkbox"
                    v-model="enableCompression"
                    :disabled="!isPro"
                    class="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div class="flex-1">
                    <div class="text-sm font-semibold text-gray-800">{{ t('compressImages') }}</div>
                    <p class="text-xs text-gray-600 mt-1">{{ t('compressImagesDesc') }}</p>
                  </div>
                </label>

                <!-- Quality slider shown when compression is enabled -->
                <div v-if="enableCompression" class="px-4 pb-4 space-y-3 border-t border-gray-200 pt-4">
                  <div>
                    <label class="block text-xs font-medium text-gray-700 mb-2">
                      {{ t('compressionQuality') }}: <span class="font-semibold">{{ compressionQuality }}%</span>
                    </label>
                    <input
                      type="range"
                      v-model.number="compressionQuality"
                      min="10"
                      max="100"
                      step="5"
                      :disabled="!isPro"
                      class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div class="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{{ t('smallerFile') }}</span>
                      <span>{{ t('higherQuality') }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Option 2: Save to Base -->
              <div class="border border-gray-300 rounded-lg bg-white">
                <label class="flex items-start gap-3 p-4 cursor-pointer hover:bg-blue-50 transition-colors">
                  <input
                    type="checkbox"
                    v-model="saveToBaseEnabled"
                    :disabled="!isPro"
                    class="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div class="flex-1">
                    <div class="text-sm font-semibold text-gray-800">{{ t('saveToBase') }}</div>
                    <p class="text-xs text-gray-600 mt-1">{{ t('saveToBaseDesc') }}</p>
                  </div>
                </label>

                <!-- Conditional fields shown when Save to Base is enabled -->
                <div v-if="saveToBaseEnabled" class="px-4 pb-4 space-y-3 border-t border-gray-200 pt-4">
                  <!-- Attachment Field Dropdown -->
                  <div>
                    <label class="block text-xs font-medium text-gray-700 mb-1">
                      {{ t('attachmentField') }} <span class="text-red-500">*</span>
                    </label>
                    <select
                      v-model="attachmentFieldId"
                      :disabled="!isPro"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      :class="{ 'bg-gray-100': !isPro }"
                    >
                      <option :value="null">{{ t('selectAttachmentField') }}</option>
                      <option v-for="field in attachmentFields" :key="field.id" :value="field.name">
                        {{ field.name }}
                      </option>
                    </select>
                  </div>

                  <!-- Attachment Write Mode -->
                  <div>
                    <label class="block text-xs font-medium text-gray-700 mb-2">
                      {{ t('attachmentWriteMode') }}
                    </label>
                    <div class="space-y-2">
                      <label class="flex items-center gap-2">
                        <input type="radio" v-model="attachmentWriteMode" value="append" :disabled="!isPro" class="w-4 h-4 text-blue-600">
                        <span class="text-sm text-gray-700">{{ t('appendToOriginal') }}</span>
                      </label>
                      <label class="flex items-center gap-2">
                        <input type="radio" v-model="attachmentWriteMode" value="replace" :disabled="!isPro" class="w-4 h-4 text-blue-600">
                        <span class="text-sm text-gray-700">{{ t('replaceOriginal') }}</span>
                      </label>
                    </div>
                  </div>

                  <!-- Status Field Dropdown (Optional) -->
                  <div>
                    <label class="block text-xs font-medium text-gray-700 mb-1">
                      {{ t('statusField') }}
                    </label>
                    <select
                      v-model="statusFieldId"
                      :disabled="!isPro"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      :class="{ 'bg-gray-100': !isPro }"
                    >
                      <option :value="null">{{ t('selectTextField') }}</option>
                      <option v-for="field in textFields" :key="field.id" :value="field.name">
                        {{ field.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Option 3: Webhook -->
              <div class="border border-gray-300 rounded-lg bg-white">
                <label class="flex items-start gap-3 p-4 cursor-pointer hover:bg-blue-50 transition-colors">
                  <input
                    type="checkbox"
                    v-model="webhookEnabled"
                    :disabled="!isPro"
                    class="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div class="flex-1">
                    <div class="text-sm font-semibold text-gray-800">{{ t('webhookNotification') }}</div>
                    <p class="text-xs text-gray-600 mt-1">{{ t('webhookNotificationDesc') }}</p>
                  </div>
                </label>

                <!-- Conditional fields shown when Webhook is enabled -->
                <div v-if="webhookEnabled" class="px-4 pb-4 space-y-3 border-t border-gray-200 pt-4">
                  <!-- Webhook Address -->
                  <div>
                    <label class="block text-xs font-medium text-gray-700 mb-1">
                      {{ t('webhookAddress') }} <span class="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      v-model="webhookUrl"
                      :disabled="!isPro"
                      placeholder="https://www.feishu.cn/base/automation/webhook/..."
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      :class="{ 'bg-gray-100': !isPro }"
                    />
                    <p class="text-xs text-gray-500 mt-1">{{ t('webhookAddressHint') }}</p>
                  </div>

                  <!-- Bearer Token -->
                  <div>
                    <label class="block text-xs font-medium text-gray-700 mb-1">
                      {{ t('webhookBearerToken') }} <span class="text-gray-500">({{ t('optional') }})</span>
                    </label>
                    <div class="relative">
                      <input
                        :type="showWebhookToken ? 'text' : 'password'"
                        v-model="webhookBearerToken"
                        :disabled="!isPro"
                        :placeholder="t('bearerTokenPlaceholder')"
                        class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        :class="{ 'bg-gray-100': !isPro }"
                      />
                      <button
                        v-if="webhookBearerToken"
                        @click="showWebhookToken = !showWebhookToken"
                        type="button"
                        class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        <Eye v-if="!showWebhookToken" :size="16" />
                        <EyeOff v-else :size="16" />
                      </button>
                    </div>
                  </div>

                  <!-- Data Structure -->
                  <div>
                    <label class="block text-xs font-medium text-gray-700 mb-1">
                      {{ t('manualSetup') }}
                    </label>
                    <p class="text-xs text-gray-500 mb-2">{{ t('samplePayloadDescription') }}</p>
                    <div v-if="sampleWebhookPayload" class="relative">
                      <pre class="bg-gray-50 border border-gray-300 rounded-lg p-3 text-xs overflow-x-auto max-h-48 overflow-y-auto font-mono">{{ JSON.stringify(sampleWebhookPayload, null, 2) }}</pre>
                      <button
                        @click="copySamplePayload"
                        type="button"
                        class="absolute top-2 right-2 px-2 py-1 bg-white border border-gray-300 rounded text-xs text-gray-600 hover:bg-gray-50"
                      >
                        {{ t('copy') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Option 4: HTTP Trigger -->
              <div class="border border-gray-300 rounded-lg bg-white" :class="{ 'opacity-60': !isHttpTriggerAvailable }">
                <!-- Warning message when dependencies not met -->
                <div v-if="!isHttpTriggerAvailable" class="bg-yellow-50 border-l-4 border-yellow-400 p-3 m-3 mb-0">
                  <div class="flex items-start gap-2">
                    <AlertCircle :size="16" class="text-yellow-600 mt-0.5 flex-shrink-0" />
                    <p class="text-xs text-yellow-800">{{ t('webhookRequiresSaveToBase') }}</p>
                  </div>
                </div>

                <div
                  class="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  :class="{ 'pointer-events-none': !isHttpTriggerAvailable }"
                  @click="isHttpTriggerAvailable && (showHttpTrigger = !showHttpTrigger)"
                >
                  <div class="mt-0.5 flex-shrink-0">
                    <ChevronDown
                      v-if="isHttpTriggerAvailable && !showHttpTrigger"
                      :size="16"
                      class="text-gray-400"
                    />
                    <ChevronUp
                      v-if="isHttpTriggerAvailable && showHttpTrigger"
                      :size="16"
                      class="text-gray-400"
                    />
                    <div v-else class="w-4 h-4"></div>
                  </div>
                  <div class="flex-1">
                    <div class="text-sm font-semibold text-gray-800">{{ t('httpTrigger') }}</div>
                    <p class="text-xs text-gray-600 mt-1">{{ t('httpTriggerDesc') }}</p>
                  </div>
                </div>

                <!-- Info section shown when HTTP Trigger is available and expanded -->
                <div v-if="isHttpTriggerAvailable && showHttpTrigger" class="px-4 pb-4 space-y-4 border-t border-gray-200 pt-4">
                  <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p class="text-xs text-blue-800 mb-3">Configure your Lark Base Automation HTTP request with these values:</p>

                    <!-- Method -->
                    <div class="mb-3">
                      <label class="block text-xs font-medium text-gray-700 mb-1">Method</label>
                      <div class="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-xs font-semibold">POST</div>
                    </div>

                    <!-- URL -->
                    <div class="mb-3">
                      <label class="block text-xs font-medium text-gray-700 mb-1">URL</label>
                      <div class="flex gap-2">
                        <input
                          type="text"
                          :value="webhookGenerateUrl"
                          readonly
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-xs font-mono"
                        />
                        <button
                          @click="copyWebhookUrl"
                          type="button"
                          class="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 text-xs"
                        >
                          <Copy :size="14" />
                        </button>
                      </div>
                    </div>

                    <!-- Params -->
                    <div class="mb-3">
                      <label class="block text-xs font-medium text-gray-700 mb-1">Params</label>
                      <div class="bg-gray-50 border border-gray-300 rounded-lg p-2">
                        <div class="flex gap-2 items-center mb-1">
                          <div class="text-xs font-mono text-gray-600 w-24">Key</div>
                          <div class="text-xs text-gray-400">:</div>
                          <div class="text-xs font-mono text-gray-600 flex-1">Value</div>
                        </div>
                        <div class="flex gap-2 items-center">
                          <div class="text-xs font-mono font-semibold w-24 bg-white border border-gray-300 rounded px-2 py-1">recordId</div>
                          <div class="text-xs text-gray-400">:</div>
                          <div class="text-xs text-gray-500 flex-1 bg-white border border-gray-300 rounded px-2 py-1">(Use record ID from automation trigger)</div>
                        </div>
                      </div>
                    </div>

                    <!-- Headers -->
                    <div v-if="tenantStore.tenant?.webhook_api_key" class="mb-3">
                      <label class="block text-xs font-medium text-gray-700 mb-1">Headers</label>
                      <div class="bg-gray-50 border border-gray-300 rounded-lg p-2">
                        <div class="flex gap-2 items-center mb-1">
                          <div class="text-xs font-mono text-gray-600 w-32">Key</div>
                          <div class="text-xs text-gray-400">:</div>
                          <div class="text-xs font-mono text-gray-600 flex-1">Value</div>
                        </div>
                        <div class="flex gap-2 items-center">
                          <div class="text-xs font-mono font-semibold w-32 bg-white border border-gray-300 rounded px-2 py-1">X-Webhook-API-Key</div>
                          <div class="text-xs text-gray-400">:</div>
                          <div class="relative flex-1">
                            <input
                              :type="showWebhookApiKey ? 'text' : 'password'"
                              :value="tenantStore.tenant.webhook_api_key"
                              readonly
                              class="w-full px-2 py-1 pr-8 border border-gray-300 rounded bg-white text-xs font-mono"
                            />
                            <button
                              @click="showWebhookApiKey = !showWebhookApiKey"
                              type="button"
                              class="absolute right-1 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              <Eye v-if="!showWebhookApiKey" :size="12" />
                              <EyeOff v-else :size="12" />
                            </button>
                          </div>
                          <button
                            @click="copyToClipboard(tenantStore.tenant.webhook_api_key)"
                            type="button"
                            class="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center"
                          >
                            <Copy :size="12" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Note if no API key -->
                    <p v-else class="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                      ⚠️ No webhook API key set. Contact admin to configure.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Save Button -->
              <button
                v-if="isPro"
                @click="saveProSettings"
                :disabled="savingProSettings"
                class="w-full px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Loader2 v-if="savingProSettings" class="animate-spin" :size="16" />
                {{ savingProSettings ? t('savingProSettings') : t('saveProSettings') }}
              </button>
            </div>

            <!-- Upgrade Button (only show if not Pro) -->
            <div v-if="!isPro" class="pt-4 border-t border-yellow-200 mt-4">
              <button
                @click="uiStore.openUpgradeModal"
                class="w-full px-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all shadow-lg">
                {{ t('upgradeToPro') }}
              </button>
              <p class="text-xs text-gray-600 text-center mt-2">{{ t('upgradeToUnlock') }}</p>
            </div>
          </div>

          <!-- Delete Template -->
          <div class="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 class="text-sm font-semibold text-red-800 mb-2">Danger Zone</h3>
            <p class="text-xs text-red-600 mb-4">Permanently delete this template</p>
            <button
              @click="handleDeleteTemplate"
              class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
            >
              <Trash2 :size="16" />
              Delete {{ props.activeTemplate?.name }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Dialog -->
    <ConfirmDialog
      v-model:show="showDeleteDialog"
      :title="t('deleteTemplateTitle')"
      :message="deleting ? t('deletingTemplate') : t('deleteTemplateMessage')"
      :confirmText="t('delete')"
      :cancelText="t('cancel')"
      :loading="deleting"
      @confirm="confirmDelete"
    />
  </div>
</template>
